import { useQueryClient } from '@tanstack/react-query'
import { WifiOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useIsOnline } from '@/lib/useConnection'

/* The lost-connection bar (UX-DR-18).
 *
 * A BAR, not a toast, and the reason is in the requirement itself: a toast that
 * vanishes during a lost connection is worse than nothing. Somebody typing into
 * a cargo checklist on a phone at a border post needs to know the connection is
 * gone for as long as it is gone — not for four seconds while they were looking
 * at the keyboard.
 *
 * It says what it says deliberately: the work is still on screen and nothing
 * has been sent. The fear a dropped connection creates is "have I lost twenty
 * minutes of typing", and answering that is more useful than naming the
 * failure.
 *
 * It never unmounts the surface behind it. A connection banner that replaced
 * the page would discard exactly the unsaved work it is reassuring the user
 * about.
 */
export function ConnectionBar() {
  const { t } = useTranslation()
  const isOnline = useIsOnline()
  const queryClient = useQueryClient()

  if (isOnline) return null

  return (
    <div
      // assertive rather than polite: this interrupts, because a user typing
      // into a form needs to know before they press save, not after.
      role="alert"
      aria-live="assertive"
      className="bg-negative-soft border-negative/25 text-text-heading flex items-center gap-3 border-b px-4 py-2 text-[13px]"
    >
      <WifiOff size={16} aria-hidden className="text-negative shrink-0" />
      <span className="min-w-0 flex-1">{t('errors.connection.offline')}</span>
      <button
        type="button"
        onClick={() => void queryClient.refetchQueries({ type: 'active' })}
        className="text-brand hover:text-brand-strong shrink-0 cursor-pointer font-medium underline-offset-2 hover:underline"
      >
        {t('errors.connection.retry')}
      </button>
    </div>
  )
}
